import React, { useMemo, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import moment from "moment";
import { twClassNames } from "../../../helpers/twClassNames";
import { TooltipContainer } from "../../Tooltip";
import Button from "../../Button";

import type { ActivityAction, ActivityActionSimple, ActivityItemShellProps } from "./types";

const CONTROLS_MIN_WIDTH = "8rem";

/** Type guard to distinguish simple `{ label, onClick }` actions from ReactNode. */
function isSimpleAction(action: ActivityAction | undefined): action is ActivityActionSimple {
    return (
        action != null &&
        typeof action === "object" &&
        !React.isValidElement(action) &&
        "label" in action &&
        "onClick" in action
    );
}

function ActivityItemShell({
    contextLabel,
    title,
    showUnreadDot = false,
    urgent = false,
    timestamp: timestampProp,
    timestampFull: timestampFullProp,
    secondary,
    onClick,
    trailing,
    onMarkRead,
    action,
    alwaysShowActions = false,
    children
}: ActivityItemShellProps) {
    const isDate = timestampProp instanceof Date;
    const timestamp = useMemo(
        () => (isDate ? moment(timestampProp).fromNow() : timestampProp),
        [isDate, timestampProp]
    );
    const timestampFull = useMemo(
        () =>
            timestampFullProp ??
            (isDate ? moment(timestampProp).format("MMM D, YYYY h:mm A") : undefined),
        [timestampFullProp, isDate, timestampProp]
    );
    const detailParts = [timestamp, secondary].filter(Boolean);
    const hasActions = showUnreadDot || !!action;
    const hasHoverActions = hasActions && !alwaysShowActions;
    const hasTrailing = !!trailing;
    const effectiveOnClick = onClick ?? (isSimpleAction(action) ? action.onClick : undefined);

    function renderMarkReadButton() {
        if (!showUnreadDot) return null;
        return (
            <TooltipContainer
                type="titleOnly"
                title="Mark as read"
                placement="top"
                customButton={
                    <Button
                        type="secondary"
                        size="xxsmall"
                        shape="square"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMarkRead?.();
                        }}
                        aria-label="Mark as read"
                    >
                        <i className="far fa-check" />
                    </Button>
                }
            />
        );
    }

    function renderActionContent() {
        if (!action) return null;
        if (isSimpleAction(action)) {
            return (
                <Button
                    type="secondary"
                    size="xxsmall"
                    onClick={(e) => {
                        e.stopPropagation();
                        action.onClick();
                    }}
                >
                    {action.label}
                </Button>
            );
        }
        return (
            <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                {action}
            </span>
        );
    }

    return (
        <div
            role={effectiveOnClick ? "button" : undefined}
            tabIndex={effectiveOnClick ? 0 : undefined}
            className={twClassNames(
                "group w-[stretch] px-4 py-3",
                effectiveOnClick && "cursor-pointer hover:bg-gray-50"
            )}
            onClick={effectiveOnClick}
            onKeyDown={
                effectiveOnClick
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              effectiveOnClick();
                          }
                      }
                    : undefined
            }
        >
            {contextLabel && (
                <p className="mb-0.5 text-xs font-normal text-gray-500">{contextLabel}</p>
            )}
            <div className="-mb-1 flex h-6 items-center">
                <AnimatePresence initial={false}>
                    {showUnreadDot && (
                        <motion.span
                            key="unread-dot"
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 14, opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: "backOut" }}
                            className="flex-shrink-0 overflow-hidden"
                            aria-hidden
                        >
                            <span
                                className={twClassNames(
                                    "block h-2 w-2 rounded-full",
                                    urgent ? "bg-yellow-500" : "bg-brand"
                                )}
                            />
                        </motion.span>
                    )}
                </AnimatePresence>
                <span
                    className={twClassNames(
                        "min-w-0 flex-1 truncate text-sm transition-[font-weight,color] duration-200",
                        showUnreadDot ? "font-semibold text-gray-900" : "font-medium text-gray-500"
                    )}
                >
                    {title}
                </span>
                {urgent && (
                    <i
                        className="far fa-circle-exclamation flex-shrink-0 text-sm text-yellow-500"
                        aria-label="Urgent"
                    />
                )}
                {(hasTrailing || hasActions) && (
                    <div
                        className="relative flex flex-shrink-0 items-center justify-end gap-1 transition-[min-width]"
                        style={hasActions ? { minWidth: CONTROLS_MIN_WIDTH } : undefined}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        {alwaysShowActions ? (
                            /* Action always visible; mark-as-read fades in on hover (or always on touch — no hover to reveal it) */
                            <div className="flex items-center justify-end gap-1">
                                {showUnreadDot && (
                                    <span
                                        className={twClassNames(
                                            "flex items-center opacity-0 transition-opacity duration-150 group-hover:opacity-100 [@media(hover:none)]:opacity-100",
                                            "pointer-events-none group-hover:pointer-events-auto [@media(hover:none)]:pointer-events-auto"
                                        )}
                                    >
                                        {renderMarkReadButton()}
                                    </span>
                                )}
                                {renderActionContent()}
                            </div>
                        ) : (
                            /* Hover-swap: trailing fades out, actions fade in.
                               Touch fallback: both trailing and actions visible inline —
                               actions become static (not absolute) so they flow beside trailing. */
                            <>
                                {hasTrailing && (
                                    <span
                                        className={twClassNames(
                                            "flex items-center transition-opacity duration-150",
                                            hasHoverActions &&
                                                "opacity-100 group-hover:opacity-0 [@media(hover:none)]:opacity-100"
                                        )}
                                    >
                                        {trailing}
                                    </span>
                                )}
                                {hasHoverActions && (
                                    <div
                                        className={twClassNames(
                                            "absolute inset-y-0 right-0 flex items-center justify-end gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100 [@media(hover:none)]:static [@media(hover:none)]:opacity-100",
                                            "pointer-events-none group-hover:pointer-events-auto [@media(hover:none)]:pointer-events-auto"
                                        )}
                                    >
                                        {renderMarkReadButton()}
                                        {renderActionContent()}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
            {detailParts.length > 0 && (
                <p className="mt-0.5 text-xs text-secondary">
                    {detailParts.map((part, i) => {
                        const isTimestamp = i === 0 && !!timestamp && !!timestampFull;
                        const content = (
                            <span key={i}>
                                {i > 0 && <span className="mx-1 text-sm text-gray-900">·</span>}
                                {part}
                            </span>
                        );
                        if (!isTimestamp) return content;
                        return (
                            <TooltipContainer
                                key={i}
                                type="titleOnly"
                                title={timestampFull}
                                placement="top"
                                delay={[400, 0]}
                                customButton={content}
                            />
                        );
                    })}
                </p>
            )}
            {children && (
                <div className="mt-1 flex flex-col gap-0.5 text-xs text-gray-500 [&>*]:truncate">
                    {children}
                </div>
            )}
        </div>
    );
}

export { isSimpleAction, ActivityItemShell };
